import env from 'config';
import {
  DEFAULT_REPLAY_SETTINGS,
  IReplayVideoSettings,
} from 'interfaces/i-replay-video-settings';
import { DeliveryType } from 'lib_ts/enums/pitches.enums';
import { RADIX_APPEARANCE_KEY, RadixAppearance } from 'lib_ts/enums/radix-ui';
import { DictOf } from 'lib_ts/interfaces/common/i-dictionary';
import { clone } from 'lodash';
import { create } from 'zustand';

const REPLAY_SETTINGS_KEY = 'replay-settings';

const resolveIsDark = (mode: RadixAppearance) => {
  switch (mode) {
    case RadixAppearance.Inherit: {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');
      return prefersDark.matches ? true : false;
    }

    case RadixAppearance.Light: {
      return false;
    }

    case RadixAppearance.Dark:
    default: {
      return true;
    }
  }
};

type MiscState = {
  isDarkMode: boolean;

  // key is a v4 ID, value is the component identifier
  dialogs: DictOf<string>;
  anyOpenDialogs: boolean;
  // key is pitch ID, value is the last delivery type used for the pitch (undefined => windup)
  deliveryDict: DictOf<DeliveryType>;

  replaySettings: IReplayVideoSettings;

  // ? temp hack for anything that might post a toast before the listener is ready
  readyToToast: boolean;
};

type MiscActions = {
  registerDialog: (config: {
    identifier: string;
    v4ID: string;
    open: boolean;
  }) => void;
  setDelivery: (pitchID: string, delivery: DeliveryType) => void;
  setReplaySettings: (value: Partial<IReplayVideoSettings>) => void;

  setAppearance: (value: RadixAppearance) => void;

  // b/c some images are different in dark mode vs light mode
  getThemeImgPath: (path: string) => string;
};

type MiscStore = MiscState & MiscActions;

const useMiscStore = create<MiscStore>((set, get) => {
  const stored = localStorage.getItem(RADIX_APPEARANCE_KEY) as RadixAppearance;

  return {
    isDarkMode: resolveIsDark(stored),
    dialogs: {},
    anyOpenDialogs: false,
    registerDialog: (config) => {
      const { dialogs } = get();
      const nextDialogs = clone(dialogs);

      if (config.open) {
        nextDialogs[config.v4ID] = config.identifier;
      } else {
        delete nextDialogs[config.v4ID];
      }

      set({
        dialogs: nextDialogs,
        anyOpenDialogs: Object.values(nextDialogs).length > 0,
      });
    },

    deliveryDict: {},
    setDelivery: (pitchID, delivery) => {
      const { deliveryDict } = get();

      set({
        deliveryDict: {
          ...deliveryDict,
          [pitchID]: delivery,
        },
      });
    },

    replaySettings: {
      ...DEFAULT_REPLAY_SETTINGS,
      ...(JSON.parse(
        localStorage.getItem(REPLAY_SETTINGS_KEY) ?? '{}'
      ) as IReplayVideoSettings),
    },
    setReplaySettings: (value) => {
      const { replaySettings } = get();
      const nextValue: IReplayVideoSettings = {
        ...replaySettings,
        ...value,
      };

      // todo: decide where we want to record this in the DB instead (if necessary)
      localStorage.setItem(REPLAY_SETTINGS_KEY, JSON.stringify(nextValue));

      set({ replaySettings: nextValue });
    },
    readyToToast: false,

    setAppearance: (value) => {
      if (!Object.values(RadixAppearance).includes(value)) {
        console.error('Invalid appearance value', value);
        return;
      }

      // for next visit
      localStorage.setItem(RADIX_APPEARANCE_KEY, value);

      // for live changes
      set({ isDarkMode: resolveIsDark(value) });
    },

    getThemeImgPath: (filepath) => {
      const today = new Date();

      if (!env.production && today.getMonth() === 3 && today.getDate() === 1) {
        return `/img/temp/${filepath}`;
      }

      return `/img/${get().isDarkMode ? 'dark' : 'light'}/${filepath}`;
    },
  };
});

export default useMiscStore;
